// components/auth/GoogleAuthFallbackComponent.tsx
"use client";

import { AlertTriangle, Chrome, ExternalLink, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface GoogleAuthFallbackProps {
  onRetry: () => void;
  onSwitchToEmail: () => void;
}

export default function GoogleAuthFallbackComponent({
  onRetry,
  onSwitchToEmail,
}: GoogleAuthFallbackProps) {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto border-orange-200 bg-orange-50">
      <CardHeader className="text-center pb-4">
        <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
        </div>
        <CardTitle className="text-lg text-orange-900">
          Google Sign-in Issue
        </CardTitle>
        <CardDescription className="text-orange-700">
          We're having trouble connecting to Google. This sometimes happens due
          to browser security settings.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-3 text-sm text-orange-800">
          <p className="font-medium">Quick fixes to try:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2">
              <Chrome className="w-4 h-4 mt-0.5 text-orange-600 flex-shrink-0" />
              <span>
                Use Chrome, Firefox, or Safari (avoid in-app browsers)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ExternalLink className="w-4 h-4 mt-0.5 text-orange-600 flex-shrink-0" />
              <span>Disable popup blockers for this site</span>
            </li>
            <li className="flex items-start gap-2">
              <RefreshCw className="w-4 h-4 mt-0.5 text-orange-600 flex-shrink-0" />
              <span>Try refreshing the page and signing in again</span>
            </li>
          </ul>
        </div>

        <div className="space-y-2 pt-2">
          <Button
            onClick={handleRetry}
            disabled={isRetrying}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isRetrying ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Retrying...
              </>
            ) : (
              "Try Google Sign-in Again"
            )}
          </Button>

          <Button
            onClick={onSwitchToEmail}
            variant="outline"
            className="w-full border-orange-300 text-orange-800 hover:bg-orange-100"
          >
            Use Email Instead
          </Button>
        </div>

        <div className="pt-2 text-xs text-orange-600 text-center">
          <p>
            This issue affects some users and we're working on a permanent fix.
            Email sign-in works reliably for everyone.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
